import React, { useEffect, useMemo } from "react";
import {
  getCodeByDescription,
  getDropdownsByID,
  getVehicleYears,
} from "../../utils/Statics";
import { StaticTypes } from "../../enums/StaticTypes";
import { useQuery } from "@tanstack/react-query";
import { Static } from "../../interfaces/Static";
import Heading from "../../components/Heading";
import CustomRadio from "../../components/CustomRadio";
import FormHeading from "../../components/FormHeading";
import CustomInput from "../../components/CustomInput";
import { useRecoilState } from "recoil";
import { vehicleDetailsState } from "../../state/QuoteState";
import { VehicleDetailsType } from "../../interfaces/VehicleDetails";
import CustomSelect from "../../components/CustomSelect";
import CustomDivider from "../../components/CustomDivider";
import { getVehicleModelDetails, getVehicleModels } from "../../api/StaticsApi";
import Loader from "../../components/Loader";
import ErrorText from "../../components/ErrorText";

const VehicleDetails = ({
  setIsFormValid,
}: {
  setIsFormValid: (value: boolean) => void;
}) => {
  const { data: statics } = useQuery<Static[]>({ queryKey: ["statics"] });

  const [
    {
      vehicleYear,
      make,
      model,
      modelDetails,
      hasMMCode,
      mmCode,
      color,
      typeOfUse,
      vehicleStatus,
    },
    setVehicleDetails,
  ] = useRecoilState(vehicleDetailsState);

  const { vehicleMakes, vehicleColors, vehicleUseTypes } = useMemo(() => {
    return {
      vehicleMakes: getDropdownsByID(
        StaticTypes.VEHICLE_MAKES,
        statics || [],
        true
      ),
      vehicleColors: getDropdownsByID(
        StaticTypes.VEHICLE_COLORS,
        statics || [],
        true
      ),
      vehicleUseTypes: getDropdownsByID(
        StaticTypes.VEHICLE_USE_TYPES,
        statics || [],
        true
      ),
    };
  }, [statics]);

  const {
    data: modelsList,
    isLoading: isModelsLoading,
    isError: isModelsError,
  } = useQuery<Static[]>({
    queryKey: ["models", vehicleYear, make],
    queryFn: () =>
      getVehicleModels(vehicleYear, getCodeByDescription(make, vehicleMakes)),
    enabled: !!vehicleYear && !!make,
  });

  const {
    data: modelDetailsList,
    isLoading: isModelDetailsLoading,
    isError: isModelDetailsError,
  } = useQuery<Static[]>({
    queryKey: ["modelDeatils", vehicleYear, make, model],
    queryFn: () =>
      getVehicleModelDetails(
        vehicleYear,
        getCodeByDescription(make, vehicleMakes),
        getCodeByDescription(model, modelsList)
      ),
    enabled: !!vehicleYear && !!make && !!model,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    function getVehicleValidation() {
      if (hasMMCode) {
        return mmCode !== "";
      } else {
        return make !== "" && model !== "" && modelDetails !== "";
      }
    }
    setIsFormValid(
      getVehicleValidation() &&
        vehicleYear !== "" &&
        color !== "" &&
        typeOfUse !== "" &&
        vehicleStatus !== null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicleYear,
    make,
    model,
    modelDetails,
    hasMMCode,
    mmCode,
    color,
    typeOfUse,
    vehicleStatus,
  ]);

  const handleInputChange = (
    value: string | boolean,
    field: keyof VehicleDetailsType
  ) => {
    setVehicleDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Heading>Vehicle Details</Heading>
      <FormHeading>Vehicle</FormHeading>
      <CustomRadio
        selected={hasMMCode}
        onSelect={(value: boolean) => {
          handleInputChange(value, "hasMMCode");
          handleInputChange("", "vehicleYear");
          handleInputChange("", "make");
          handleInputChange("", "model");
          handleInputChange("", "modelDetails");
          handleInputChange("", "mmCode");
        }}
        name="mmCode"
        required
      >
        Do you have a MM Code?
      </CustomRadio>
      {hasMMCode === true && (
        <>
          <CustomInput
            placeholder="MM Code"
            value={mmCode}
            onChange={(value) => {
              handleInputChange(value, "mmCode");
            }}
            max={40}
            required
          />
          <CustomSelect
            items={getVehicleYears()}
            value={vehicleYear}
            placeholder="Year"
            onSelect={(value: string) => {
              handleInputChange(value, "vehicleYear");
            }}
            required
          />
        </>
      )}
      {hasMMCode === false && (
        <>
          <CustomSelect
            items={vehicleMakes}
            value={make}
            placeholder="Make"
            onSelect={(value: string) => {
              handleInputChange(value, "make");
              handleInputChange("", "model");
              handleInputChange("", "modelDetails");
            }}
            required
          />
          <CustomSelect
            items={getVehicleYears()}
            value={vehicleYear}
            placeholder="Year"
            onSelect={(value: string) => {
              handleInputChange(value, "vehicleYear");
              handleInputChange("", "model");
              handleInputChange("", "modelDetails");
            }}
            required
          />
          {isModelsLoading && <Loader />}
          {modelsList && (
            <>
              {modelsList.length === 0 ? (
                <ErrorText>
                  There are no available models for this selection!
                </ErrorText>
              ) : (
                <CustomSelect
                  items={modelsList.sort((a: Static, b: Static) =>
                    a.description.localeCompare(b.description)
                  )}
                  value={model}
                  placeholder="Model"
                  onSelect={(value: string) => {
                    handleInputChange(value, "model");
                    handleInputChange(
                      getCodeByDescription(value, modelsList),
                      "modelCode"
                    );
                  }}
                  required
                />
              )}
            </>
          )}
          {isModelsError && (
            <ErrorText>
              The call was not successful. Please try again, or later.
            </ErrorText>
          )}
          {isModelDetailsLoading && <Loader />}
          {modelDetailsList && (
            <>
              {modelDetailsList.length === 0 ? (
                <ErrorText>
                  There are no available model details for this selection!
                </ErrorText>
              ) : (
                <CustomSelect
                  items={modelDetailsList.sort((a: Static, b: Static) =>
                    a.description.localeCompare(b.description)
                  )}
                  value={modelDetails}
                  placeholder="Model details"
                  onSelect={(value: string) => {
                    handleInputChange(value, "modelDetails");
                    handleInputChange(
                      getCodeByDescription(value, modelDetailsList),
                      "mmCode"
                    );
                  }}
                  required
                />
              )}
            </>
          )}
          {isModelDetailsError && (
            <ErrorText>
              The call was not successful. Please try again, or later.
            </ErrorText>
          )}
        </>
      )}
      <CustomDivider />

      <FormHeading>Exterior</FormHeading>
      <CustomSelect
        items={vehicleColors}
        value={color}
        placeholder="Color"
        onSelect={(value: string) => {
          handleInputChange(value, "color");
        }}
        required
      />

      <CustomDivider />

      <FormHeading>Vehicle use</FormHeading>
      <CustomSelect
        items={vehicleUseTypes}
        value={typeOfUse}
        placeholder="Type of use"
        onSelect={(value: string) => {
          handleInputChange(value, "typeOfUse");
        }}
        required
      />

      <CustomDivider />

      <FormHeading>Vehicle status</FormHeading>
      <CustomRadio
        selected={vehicleStatus}
        onSelect={(value: boolean) => handleInputChange(value, "vehicleStatus")}
        name="vehicelStatus"
        customValues={["Used", "New"]}
        required
      >
        What is the vehicle status?
      </CustomRadio>
    </>
  );
};

export default VehicleDetails;
